
import moment from "moment";
import Vue from "vue";
import OrderInlineDetail from "./OrderInlineDetail.vue";
import { exportCsv } from "@/utils/csv";
import { loadPgFeeRates, calculatePgFeeForItem } from "@/utils/pgFee";
import {
  getActualOrderCompositionAmount,
  getInitialOrderCompositionAmount,
} from "@/utils/orderPaymentSummary";
import { summarizeRefundAmounts } from "@/utils/refundAmountSummary";
import eventBus from "@/eventBus";
import {
  collectOrderPathOptions,
  getNaverPoint,
  isNaverPayFilterActive,
  isNaverPayOrder,
  OrderPathFilter,
  OrderPathOption,
  resolveOrderPath,
} from "@/utils/orderPathFilter";
import {
  getOrderStatusText,
  getOrderStatusBadgeClass,
} from "@/utils/orderStatus";

moment.locale("ko");

type SettlementGroup = {
  order_place: string;
  order_payment_method: string;
  refund_method: string;
  items: any[];
};

type TableRow = {
  type: "group" | "item";
  key: string;
  group: SettlementGroup;
  item?: any;
};

type OrderStatusChip = {
  key: string;
  statusText: string;
  count: number;
  badgeClass: string;
  chipText: string;
  title: string;
};

type SortKey =
  | "orderId"
  | "paymentDate"
  | "refundDate"
  | "orderPlace"
  | "productName"
  | "paymentMethod"
  | "refundMethod"
  | "orderCount"
  | "refundCount"
  | "paymentTotal"
  | "naverPoint"
  | "refundTotal"
  | "netSales"
  | "pgFee"
  | "netSettlement"
  | "feeStatus";

type SortDirection = "asc" | "desc";
type SortValue = string | number | null;

type SortableColumn = {
  key: SortKey;
  label: string;
  align?: "right" | "center";
};

const SORTABLE_COLUMNS: SortableColumn[] = [
  { key: "orderId", label: "주문번호" },
  { key: "paymentDate", label: "결제일시" },
  { key: "refundDate", label: "환불일시" },
  { key: "orderPlace", label: "주문경로" },
  { key: "productName", label: "상품명" },
  { key: "paymentMethod", label: "결제방식" },
  { key: "refundMethod", label: "환불방식" },
  { key: "orderCount", label: "결제수", align: "right" },
  { key: "refundCount", label: "환불수", align: "right" },
  { key: "paymentTotal", label: "결제 합계", align: "right" },
  { key: "refundTotal", label: "환불 합계", align: "right" },
  { key: "netSales", label: "순매출", align: "right" },
  { key: "pgFee", label: "예상 수수료", align: "right" },
  { key: "netSettlement", label: "예상 정산 금액", align: "right" },
  { key: "feeStatus", label: "계산상태", align: "center" },
];

const PAGE_SIZE_OPTIONS = [1000, 500, 100] as const;

export default Vue.extend({
  components: {
    OrderInlineDetail,
  },
  props: {
    groups: {
      type: Array as () => SettlementGroup[],
      default: () => [],
    },
    orderPathFilter: {
      type: Object as () => OrderPathFilter,
      default: undefined,
    },
  },
  data(): {
    search: string;
    currentPage: number;
    pageSize: number;
    pageSizeOptions: number[];
    expandedGroups: Record<string, boolean>;
    expandedOrders: Record<string, boolean>;
    sortKey: SortKey | null;
    sortDirection: SortDirection;
    sortableColumns: SortableColumn[];
  } {
    return {
      search: "",
      currentPage: 1,
      pageSize: 1000,
      pageSizeOptions: [...PAGE_SIZE_OPTIONS],
      expandedGroups: {},
      expandedOrders: {},
      sortKey: null,
      sortDirection: "asc",
      sortableColumns: SORTABLE_COLUMNS,
    };
  },
  filters: {
    formatDate(date: string): string {
      if (!date) return "";
      return moment(date).format("YYYY-MM-DD HH:mm:ss");
    },
  },
  computed: {
    hasBaseDomain(): boolean {
      return Boolean(this.$store.state.shop.base_domain);
    },
    visibleGroups(): SettlementGroup[] {
      const query = this.search.toLowerCase().trim();
      if (!query) return this.groups;

      return this.groups.reduce((visible: SettlementGroup[], group) => {
        const groupMatches = [
          group.order_place,
          group.order_payment_method,
          group.refund_method,
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(query)
        );
        const items = groupMatches
          ? group.items
          : group.items.filter((item) => this.itemMatchesQuery(item, query));
        if (items.length > 0) visible.push({ ...group, items });
        return visible;
      }, []);
    },
    sortedGroups(): SettlementGroup[] {
      const groups = this.visibleGroups.map((group) => ({
        ...group,
        items: this.sortGroupItems(group),
      }));
      if (!this.sortKey) return groups;

      return groups
        .map((group, index) => ({ group, index }))
        .sort((a, b) => {
          const comparison = this.compareSortValues(
            this.groupSortValue(a.group, this.sortKey as SortKey),
            this.groupSortValue(b.group, this.sortKey as SortKey)
          );
          return comparison || a.index - b.index;
        })
        .map(({ group }) => group);
    },
    filteredItems(): any[] {
      return this.sortedGroups.reduce(
        (items: any[], group) => items.concat(group.items),
        []
      );
    },
    tableRows(): TableRow[] {
      const rows: TableRow[] = [];
      this.sortedGroups.forEach((group) => {
        const groupKey = this.groupKey(group);
        rows.push({ type: "group", key: `group:${groupKey}`, group });
        if (this.expandedGroups[groupKey] || this.search.trim()) {
          group.items.forEach((item, index) => {
            rows.push({
              type: "item",
              key: `item:${groupKey}:${index}:${item?.order_id || ""}`,
              group,
              item,
            });
          });
        }
      });
      return rows;
    },
    totalPages(): number {
      return Math.ceil(this.tableRows.length / this.pageSize) || 1;
    },
    paginatedRows(): TableRow[] {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.tableRows.slice(start, start + this.pageSize);
    },
    hasExpandedGroups(): boolean {
      return this.visibleGroups.some(
        (group) => this.expandedGroups[this.groupKey(group)]
      );
    },
    allExpanded(): boolean {
      return (
        this.visibleGroups.length > 0 &&
        this.visibleGroups.every(
          (group) => this.expandedGroups[this.groupKey(group)]
        )
      );
    },
    totalOrderCount(): number {
      return this.filteredItems.filter(
        (item) => !Array.isArray(item?.refund_methods)
      ).length;
    },
    totalRefundCount(): number {
      return this.filteredItems.filter((item) =>
        Array.isArray(item?.refund_methods)
      ).length;
    },
    totalOrderCompositionAmount(): number {
      return this.filteredItems.reduce(
        (sum, item) => sum + this.initialOrderAmount(item),
        0
      );
    },
    totalRefundAmount(): number {
      return summarizeRefundAmounts(this.filteredItems).amount;
    },
    totalPgFee(): number {
      return this.filteredItems.reduce(
        (sum, item) => sum + this.getFeeCalculation(item).fee,
        0
      );
    },
    totalNetSales(): number {
      return this.totalOrderCompositionAmount + this.totalRefundAmount;
    },
    totalNetSettlement(): number {
      return this.totalNetSales - this.totalPgFee;
    },
    orderPathOptions(): OrderPathOption[] {
      const items: any[] = [];
      this.groups.forEach((group) => {
        if (Array.isArray(group.items)) items.push(...group.items);
      });
      return collectOrderPathOptions(
        items,
        this.orderPathFilter?.presentationMode
      );
    },
    isNaverPayFilterActive(): boolean {
      return isNaverPayFilterActive(
        this.orderPathFilter,
        this.orderPathOptions
      );
    },
    activeSortableColumns(): SortableColumn[] {
      if (!this.isNaverPayFilterActive) {
        return this.sortableColumns;
      }
      const paymentIndex = this.sortableColumns.findIndex(
        (col) => col.key === "paymentTotal"
      );
      if (paymentIndex === -1) {
        return [
          ...this.sortableColumns,
          { key: "naverPoint", label: "네이버 포인트", align: "right" },
        ];
      }
      const cols = [...this.sortableColumns];
      cols.splice(paymentIndex + 1, 0, {
        key: "naverPoint",
        label: "네이버 포인트",
        align: "right",
      });
      return cols;
    },
    totalColspan(): number {
      return this.isNaverPayFilterActive ? 17 : 16;
    },
    totalNaverPoint(): number {
      return this.filteredItems.reduce((sum, item) => {
        return sum + (this.isItemNaverPay(item) ? getNaverPoint(item) : 0);
      }, 0);
    },
  },
  watch: {
    search() {
      this.currentPage = 1;
    },
    groups() {
      this.expandedGroups = {};
      this.expandedOrders = {};
      this.currentPage = 1;
    },
  },
  methods: {
    toggleSort(key: SortKey): void {
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortDirection = "asc";
      }
      this.currentPage = 1;
    },
    sortIcon(key: SortKey): string {
      if (this.sortKey !== key) return "mdi-swap-vertical";
      return this.sortDirection === "asc" ? "mdi-arrow-up" : "mdi-arrow-down";
    },
    sortAria(key: SortKey): "none" | "ascending" | "descending" {
      if (this.sortKey !== key) return "none";
      return this.sortDirection === "asc" ? "ascending" : "descending";
    },
    sortButtonTitle(column: SortableColumn): string {
      const nextDirection =
        this.sortKey === column.key && this.sortDirection === "asc"
          ? "내림차순"
          : "오름차순";
      return `${column.label} ${nextDirection} 정렬`;
    },
    sortGroupItems(group: SettlementGroup): any[] {
      if (!this.sortKey) return group.items;
      return group.items
        .map((item, index) => ({ item, index }))
        .sort((a, b) => {
          const comparison = this.compareSortValues(
            this.itemSortValue(a.item, group, this.sortKey as SortKey),
            this.itemSortValue(b.item, group, this.sortKey as SortKey)
          );
          return comparison || a.index - b.index;
        })
        .map(({ item }) => item);
    },
    groupSortValue(group: SettlementGroup, key: SortKey): SortValue {
      if (
        ["orderId", "paymentDate", "refundDate", "productName"].includes(key)
      ) {
        const values = group.items
          .map((item) => this.itemSortValue(item, group, key))
          .filter((value) => !this.isEmptySortValue(value));
        if (values.length === 0) return null;

        return values.reduce((selected, value) => {
          const comparison = this.compareBaseSortValues(value, selected);
          const shouldReplace =
            this.sortDirection === "asc" ? comparison < 0 : comparison > 0;
          return shouldReplace ? value : selected;
        });
      }

      return this.rowSortValue(
        { type: "group", key: this.groupKey(group), group },
        key
      );
    },
    itemSortValue(item: any, group: SettlementGroup, key: SortKey): SortValue {
      return this.rowSortValue(
        { type: "item", key: String(item?.order_id || ""), group, item },
        key
      );
    },
    rowSortValue(row: TableRow, key: SortKey): SortValue {
      switch (key) {
        case "orderId":
          return row.type === "item" ? String(row.item?.order_id || "") : null;
        case "paymentDate":
          return row.type === "item"
            ? this.dateSortValue(row.item?.payment_date)
            : null;
        case "refundDate":
          return row.type === "item"
            ? this.dateSortValue(row.item?.refund_date)
            : null;
        case "orderPlace":
          return this.rowOrderPlace(row);
        case "productName":
          return row.type === "item" ? this.productName(row.item) : null;
        case "paymentMethod":
          return this.rowPaymentMethod(row);
        case "refundMethod":
          return this.rowRefundMethod(row);
        case "orderCount":
          return this.rowOrderCount(row);
        case "refundCount":
          return this.rowRefundCount(row);
        case "paymentTotal":
          return this.rowInitialAmount(row);
        case "naverPoint":
          return this.rowNaverPoint(row);
        case "refundTotal":
          return Math.abs(this.rowRefundAmount(row));
        case "netSales":
          return this.rowNetSales(row);
        case "pgFee":
          return this.rowPgFee(row);
        case "netSettlement":
          return this.rowFeeStatus(row) === "unavailable"
            ? null
            : this.rowNetSettlement(row);
        case "feeStatus":
          return this.feeStatusLabel(this.rowFeeStatus(row));
      }
    },
    dateSortValue(value: string): number | null {
      if (!value) return null;
      const timestamp = moment(value).valueOf();
      return Number.isFinite(timestamp) ? timestamp : null;
    },
    feeStatusLabel(status: string): string {
      return (
        {
          estimated: "추정",
          unavailable: "미계산",
          refund: "해당 없음",
          exact: "확인",
        }[status] || status
      );
    },
    isEmptySortValue(value: SortValue): boolean {
      return (
        value === null || (typeof value === "string" && value.trim() === "")
      );
    },
    compareBaseSortValues(a: SortValue, b: SortValue): number {
      if (typeof a === "number" && typeof b === "number") return a - b;
      return String(a).localeCompare(String(b), "ko", {
        numeric: true,
        sensitivity: "base",
      });
    },
    compareSortValues(a: SortValue, b: SortValue): number {
      const aIsEmpty = this.isEmptySortValue(a);
      const bIsEmpty = this.isEmptySortValue(b);
      if (aIsEmpty && bIsEmpty) return 0;
      if (aIsEmpty) return 1;
      if (bIsEmpty) return -1;

      const comparison = this.compareBaseSortValues(a, b);
      return this.sortDirection === "asc" ? comparison : -comparison;
    },
    groupKey(group: SettlementGroup): string {
      return JSON.stringify([
        group.order_place || "",
        group.order_payment_method || "",
      ]);
    },
    itemMatchesQuery(item: any, query: string): boolean {
      const statusTexts = this.rowOrderItems(item).map((i) =>
        this.getItemStatusText(i)
      );
      return [
        item?.order_id,
        this.productName(item),
        resolveOrderPath(item, this.orderPathFilter?.presentationMode).label,
        item?.order_place_name || item?.order?.order_place_name,
        item?.payment_method_name || item?.order?.payment_method_name,
        this.formatRefundMethod(item),
        ...statusTexts,
      ].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(query)
      );
    },
    isGroupExpanded(group: SettlementGroup): boolean {
      return Boolean(this.expandedGroups[this.groupKey(group)]);
    },
    isOrderExpanded(row: TableRow): boolean {
      return Boolean(this.expandedOrders[row.key]);
    },
    toggleOrder(rowKey: string): void {
      this.$set(this.expandedOrders, rowKey, !this.expandedOrders[rowKey]);
    },
    handleRowClick(row: TableRow, event?: MouseEvent): void {
      if (row.type === "group") {
        this.toggleGroup(row.group, event?.currentTarget as HTMLElement);
      } else {
        this.toggleOrder(row.key);
      }
    },
    toggleGroup(group: SettlementGroup, targetEl?: HTMLElement): void {
      const key = this.groupKey(group);
      const expanded = !this.expandedGroups[key];
      this.$set(this.expandedGroups, key, expanded);
      if (expanded) {
        this.$emit("open-group", group);
        if (targetEl && typeof targetEl.scrollIntoView === "function") {
          this.$nextTick(() => {
            targetEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
          });
        }
      } else {
        const remainingOpen = this.visibleGroups.filter(
          (g) => this.expandedGroups[this.groupKey(g)]
        );
        if (remainingOpen.length > 0) {
          this.$emit("open-group", remainingOpen[remainingOpen.length - 1]);
        } else {
          this.$emit("close-all");
        }
      }
      this.clampCurrentPage();
    },
    expandGroup(group: SettlementGroup, emitEvent = false): void {
      this.$set(this.expandedGroups, this.groupKey(group), true);
      this.clampCurrentPage();
      if (emitEvent) this.$emit("open-group", group);
    },
    expandAll(emitEvent = true): void {
      const expanded: Record<string, boolean> = {};
      this.visibleGroups.forEach((group) => {
        expanded[this.groupKey(group)] = true;
      });
      this.expandedGroups = expanded;
      this.currentPage = 1;
      if (emitEvent) this.$emit("open-all");
    },
    collapseAll(emitEvent = true): void {
      this.expandedGroups = {};
      this.expandedOrders = {};
      this.currentPage = 1;
      if (emitEvent) this.$emit("close-all");
    },
    handlePageSizeChange(newSize: number): void {
      if (!newSize || newSize <= 0) return;
      const currentStartIndex = (this.currentPage - 1) * this.pageSize;
      this.pageSize = newSize;
      const newTotalPages = Math.ceil(this.tableRows.length / newSize) || 1;
      const targetPage = Math.floor(currentStartIndex / newSize) + 1;
      this.currentPage = Math.max(1, Math.min(targetPage, newTotalPages));
      this.clampCurrentPage();
    },
    clampCurrentPage(): void {
      this.$nextTick(() => {
        this.currentPage = Math.max(
          1,
          Math.min(this.currentPage, this.totalPages)
        );
      });
    },
    rowItems(row: TableRow): any[] {
      return row.type === "group" ? row.group.items : [row.item];
    },
    rowOrderPlace(row: TableRow): string {
      return row.type === "group"
        ? row.group.order_place
        : resolveOrderPath(row.item, this.orderPathFilter?.presentationMode)
            .label;
    },
    rowPaymentMethod(row: TableRow): string {
      return row.type === "group"
        ? row.group.order_payment_method
        : String(
            row.item?.payment_method_name ||
              row.item?.order?.payment_method_name ||
              ""
          );
    },
    rowRefundMethod(row: TableRow): string {
      return row.type === "group"
        ? row.group.refund_method
        : this.formatRefundMethod(row.item);
    },
    rowOrderCount(row: TableRow): number {
      return this.rowItems(row).filter(
        (item) => !Array.isArray(item?.refund_methods)
      ).length;
    },
    rowRefundCount(row: TableRow): number {
      return this.rowItems(row).filter((item) =>
        Array.isArray(item?.refund_methods)
      ).length;
    },
    rowInitialAmount(row: TableRow): number {
      return this.rowItems(row).reduce(
        (sum, item) => sum + this.initialOrderAmount(item),
        0
      );
    },
    rowRefundAmount(row: TableRow): number {
      return summarizeRefundAmounts(this.rowItems(row)).amount;
    },
    rowPgFee(row: TableRow): number {
      return this.rowItems(row).reduce(
        (sum, item) => sum + this.getFeeCalculation(item).fee,
        0
      );
    },
    rowNetSales(row: TableRow): number {
      return this.rowInitialAmount(row) + this.rowRefundAmount(row);
    },
    rowNetSettlement(row: TableRow): number {
      return this.rowNetSales(row) - this.rowPgFee(row);
    },
    rowFeeStatus(row: TableRow): string {
      const items = this.rowItems(row);
      if (items.every((item) => Array.isArray(item?.refund_methods))) {
        return "refund";
      }
      const statuses = items
        .filter((item) => !Array.isArray(item?.refund_methods))
        .map((item) => this.getFeeCalculation(item).status);
      if (statuses.includes("unavailable")) return "unavailable";
      if (statuses.includes("estimated")) return "estimated";
      return "exact";
    },
    actualOrderAmount(item: any): number {
      return getActualOrderCompositionAmount(item);
    },
    initialOrderAmount(item: any): number {
      return getInitialOrderCompositionAmount(item);
    },
    formatRefundMethod(item: any): string {
      if (!item?.refund_methods) return "";
      return item.refund_methods.length > 0 && item.refund_methods[0] == null
        ? "정보 없음"
        : item.refund_methods.toString();
    },
    getFeeCalculation(item: any): any {
      if (Array.isArray(item?.refund_methods)) {
        return {
          fee: 0,
          status: "refund",
          breakdown: { components: [] },
          componentFees: [],
        };
      }
      return calculatePgFeeForItem(
        item,
        loadPgFeeRates(),
        item,
        this.orderPathFilter?.presentationMode
      );
    },
    productName(item: any): string {
      if (item?.items?.length > 0) {
        return (
          item.items[0].product_name +
          (item.items.length > 1 ? ` 외 ${item.items.length - 1}개` : "")
        );
      }
      if (item?.order?.items?.length > 0) {
        return (
          item.order.items[0].product_name +
          (item.order.items.length > 1
            ? ` 외 ${item.order.items.length - 1}개`
            : "")
        );
      }
      if (item?.product_name) {
        return item.product_name;
      }
      return "";
    },
    rowOrderItems(item: any): any[] {
      if (Array.isArray(item?.items) && item.items.length > 0) {
        return item.items;
      }
      if (Array.isArray(item?.order?.items) && item.order.items.length > 0) {
        return item.order.items;
      }
      if (
        item &&
        (item.order_status ||
          item.status_code ||
          item.status ||
          item.status_text)
      ) {
        return [item];
      }
      return [];
    },
    getItemStatusText(orderItem: any): string {
      return getOrderStatusText(orderItem);
    },
    getItemStatusBadgeClass(orderItem: any): string {
      return getOrderStatusBadgeClass(orderItem);
    },
    getItemStatusTitle(orderItem: any): string {
      const statusText = getOrderStatusText(orderItem);
      const name = orderItem?.product_name
        ? String(orderItem.product_name)
        : "";
      return name ? `${name}: ${statusText}` : statusText;
    },
    rowOrderStatusChips(item: any): OrderStatusChip[] {
      const items = this.rowOrderItems(item);
      if (items.length === 0) return [];

      const map = new Map<
        string,
        {
          statusText: string;
          badgeClass: string;
          count: number;
          productNames: string[];
        }
      >();

      items.forEach((orderItem) => {
        const statusText = this.getItemStatusText(orderItem);
        const badgeClass = this.getItemStatusBadgeClass(orderItem);
        const groupKey = `${statusText}:::${badgeClass}`;
        const productName = orderItem?.product_name
          ? String(orderItem.product_name).trim()
          : "";

        const existing = map.get(groupKey);
        if (existing) {
          existing.count += 1;
          if (productName && !existing.productNames.includes(productName)) {
            existing.productNames.push(productName);
          }
        } else {
          map.set(groupKey, {
            statusText,
            badgeClass,
            count: 1,
            productNames: productName ? [productName] : [],
          });
        }
      });

      const chips: OrderStatusChip[] = [];
      map.forEach((value, key) => {
        const chipText =
          value.count > 1
            ? `${value.statusText} ${value.count}`
            : value.statusText;
        let title =
          value.count > 1
            ? `${value.statusText} (${value.count}개)`
            : value.statusText;
        if (value.productNames.length > 0) {
          title += `: ${value.productNames.join(", ")}`;
        }
        chips.push({
          key,
          statusText: value.statusText,
          count: value.count,
          badgeClass: value.badgeClass,
          chipText,
          title,
        });
      });

      return chips;
    },
    selectOrder(item: any): void {
      if (!this.$store.state.shop.base_domain) {
        eventBus.$emit("snackbar", {
          message: "상점 정보 준비 후 주문 상세를 열 수 있습니다.",
        });
        return;
      }
      const url = `https://${this.$store.state.shop.base_domain}/admin/php/shop1/s_new/order_detail.php?order_id=${item.order_id}`;
      window.open(
        url,
        "popup",
        "width=1000,height=600,left=100,top=100,toolbar=no,location=no,menubar=no"
      );
    },
    isItemNaverPay(item: any): boolean {
      return isNaverPayOrder(item, this.orderPathFilter?.presentationMode);
    },
    isGroupNaverPay(group: SettlementGroup): boolean {
      if (!group) return false;
      const place = String(group.order_place || "").toLowerCase();
      if (place.includes("네이버") || place.includes("naver")) return true;
      return (
        Array.isArray(group.items) &&
        group.items.some((item) => this.isItemNaverPay(item))
      );
    },
    isRowNaverPay(row: TableRow): boolean {
      return this.rowItems(row).some((item) => this.isItemNaverPay(item));
    },
    rowNaverPoint(row: TableRow): number {
      return this.rowItems(row).reduce(
        (sum, item) =>
          sum + (this.isItemNaverPay(item) ? getNaverPoint(item) : 0),
        0
      );
    },
    exportCsv(): void {
      if (this.filteredItems.length === 0) return;
      const headers = [
        "주문번호",
        "결제일시",
        "판매처",
        "상품명",
        "결제수단",
        "환불수단",
        "결제 합계",
        ...(this.isNaverPayFilterActive ? ["네이버 포인트"] : []),
        "현재 결제 합계",
        "차이 금액",
        "환불 합계",
        "순매출",
        "예상 수수료",
        "계산상태",
        "예상 정산 금액",
        "환불일시",
      ];
      const rows = this.filteredItems.map((item: any) => {
        const initialPaymentPrice = this.initialOrderAmount(item);
        const actualPaymentPrice = this.actualOrderAmount(item);
        const refundAmount = summarizeRefundAmounts([item]).amount;
        const netSales = initialPaymentPrice + refundAmount;
        const feeCalculation = this.getFeeCalculation(item);
        const naverPoint = this.isItemNaverPay(item) ? getNaverPoint(item) : 0;
        return [
          item.order_id,
          item.payment_date || "",
          resolveOrderPath(item, this.orderPathFilter?.presentationMode).label,
          this.productName(item),
          String(
            item.payment_method_name || item.order?.payment_method_name || ""
          ),
          this.formatRefundMethod(item),
          initialPaymentPrice,
          ...(this.isNaverPayFilterActive ? [naverPoint] : []),
          actualPaymentPrice,
          initialPaymentPrice - actualPaymentPrice,
          Math.abs(refundAmount),
          netSales,
          feeCalculation.fee,
          feeCalculation.status,
          netSales - feeCalculation.fee,
          item.refund_date || "",
        ];
      });
      exportCsv(
        headers,
        rows,
        `정산_주문상세_${moment().format("YYYYMMDD_HHmmss")}.csv`
      );
    },
  },
});
